// Regras de negócio do formulário de "Novo compromisso" — compartilhadas pela `NewAppointmentSidebar`
// (7 telas a usam, inclusive para reagendar) e pela página cheia `appointment-new`
// (`src/modules/appointment_report/views/NewAppointmentPage.vue`).
//
// Aqui ficam só o ESTADO do formulário, o carregamento das opções (tipos, formatos, PNs, especialistas,
// clientes), a validação (Vuelidate) e a montagem/envio do `POST /api/appointments`. O que é de cada tela
// — abrir/fechar a sidebar, preencher o reagendamento, o layout, o que fazer depois de salvar e como mostrar
// os erros de regra — continua em cada componente.
//
// Contrato com quem usa o mixin:
//  - `setup()` já cria `toast`, `v$` e `skin` (a composition-api mescla o `setup` do mixin com o do componente;
//    o componente não deve chamar `useVuelidate()` de novo);
//  - `customer` é o id do cliente (ou uma lista de ids na sidebar, para agendamentos múltiplos);
//  - `appointmentStart` / `appointmentEnd` usam o formato do flatpickr da sidebar, "DD/MM/YYYY HH:mm";
//  - `consultant` é o PN Responsável (o `appify_consultant_id` do usuário logado).
import _ from 'lodash'
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import { stringToDateTime } from '@/helpers/converters'
import { NEW_COPY } from '@/constants/appointment_report_v2'
import * as sharedTypes from '@/modules/shared/store/types'
import * as accountTypes from '@/modules/account/store/types'
import * as appointmentTypes from '@/constants/appointment_types'
import * as customerType from '@/constants/customers_types'
import * as types from '../store/types'

// Formato de reunião presencial — grafia do backend (`AppointmentMeetingFormatTypes::IN_PEARSON`).
export const IN_PERSON_MEETING_FORMAT = 'in_pearson'
export const ONLINE_MEETING_FORMAT = 'online'

// Validação de URL da reunião online (mesma regra que a sidebar sempre usou).
const MEETING_URL_PATTERN = /^(https?:\/\/)?(www\.)?([a-z0-9]+([.-][a-z0-9]+)*\.)([a-z]{2,}|[a-z]{2,}\.[a-z]{2,})(:[0-9]{1,5})?(\/.*)?$/i

// Parâmetro de `GET /api/consultants/summary/specialists` para cada tipo de compromisso.
export const specialistParamsForEventType = eventType => {
  if (eventType === appointmentTypes.INTERVIEW) return { interview: true }
  if (eventType === appointmentTypes.X_MOMENT) return { momentx: true }
  if (eventType === appointmentTypes.PARTNERSHIP) return { partnership: true }

  return {}
}

const errorToastProps = text => ({
  title: 'Oops!',
  text,
  icon: 'AlertTriangleIcon',
  variant: 'danger',
})

export default {
  setup() {
    const { skin } = useAppConfig()
    return { toast: useToast(), v$: useVuelidate(), skin }
  },
  data() {
    return {
      loading: {
        customers: false,
        eventTypes: false,
        meetingFormatTypes: false,
        consultants: false,
        specialistConsultants: false,
      },
      id: undefined,
      customer: undefined,
      customerEmail: undefined,
      eventType: undefined,
      meetingFormat: undefined,
      consultant: undefined,
      consultantSpecialist: undefined,
      appointmentStart: undefined,
      appointmentEnd: undefined,
      roomLink: undefined,
      description: undefined,
      saving: false,
      consultantSpecialistId: null,
      // Reagendamento: o início que já estava gravado. Pode ter passado (compromisso atrasado) — só um horário NOVO
      // precisa ser futuro, como no `PUT /api/appointments/{id}`.
      originalAppointmentStart: undefined,
      // "Agora" da regra do início no passado (a validação só reage a dados; `refreshNow()` antes de validar).
      nowTick: Date.now(),
    }
  },
  validations() {
    return {
      customer: { required },
      customerEmail: {
        required: requiredIf(() => {
          if (!this.hasEmail) {
            return true
          }
          return false
        }),
      },
      eventType: { required },
      meetingFormat: { required },
      consultant: { required },
      consultantSpecialist: {},
      appointmentStart: {
        required,
        notPast(value) {
          return !this.isStartInPast(value)
        },
      },
      appointmentEnd: {
        required,
        valid(date) {
          if (this.appointmentStart) {
            const start = moment(this.appointmentStart, 'DD/MM/YYYY hh:mm')
            const end = moment(date, 'DD/MM/YYYY hh:mm')
            return end > start
          }
          return true
        },
      },
      roomLink: {
        required,
        validUrl(value) {
          // Reunião presencial: o campo é o endereço, não uma URL.
          if (this.meetingFormat === IN_PERSON_MEETING_FORMAT) {
            return true
          }
          // Sem valor, o `required` já cuida.
          if (!value) {
            return true
          }
          // Reunião online: precisa ser uma URL válida.
          try {
            return MEETING_URL_PATTERN.test(value)
          } catch (error) {
            return false
          }
        },
      },
      description: {},
    }
  },
  computed: {
    ...mapGetters({
      appointmentEventTypes: types.APPOINTMENT_EVENT_TYPES,
      appointmentMeetingFormatTypes: types.APPOINTMENT_MEETING_FORMAT_TYPES,
      customers: types.APPOINTMENT_CUSTOMERS,
      consultants: sharedTypes.CONSULTANTS,
      specialistConsultants: sharedTypes.SPECIALIST_CONSULTANTS,
      user: accountTypes.USER,
    }),
    // Mensagem do início no passado (o mesmo texto do 405 do servidor).
    startInPastMessage() {
      return NEW_COPY.startInPast
    },
    // Cliente escolhido (só no agendamento de um cliente); `undefined` sem cliente ou fora da lista carregada.
    selectedCustomer() {
      return _.find(this.customers, { id: this.customer })
    },
    isLead() {
      const selected = this.selectedCustomer
      if (selected !== undefined) {
        return selected.customer_type_id == customerType.LEAD // eslint-disable-line eqeqeq
      }
      return undefined
    },
    isLeadPN() {
      const selected = this.selectedCustomer
      if (selected !== undefined) {
        return selected.customer_type_id == customerType.LEAD_PN // eslint-disable-line eqeqeq
      }
      return undefined
    },
    hasEpRunning() {
      const selected = this.selectedCustomer
      if (selected !== undefined) {
        return !!selected.has_ep_running
      }
      return undefined
    },
    hasEmail() {
      if (this.customer && !this.hasMultipleCustomers) {
        const selected = this.selectedCustomer
        if (selected && (!selected.email || selected.email === '')) {
          return false
        }
      }
      return true
    },
    hasMultipleCustomers() {
      if (typeof this.customer === 'object' && this.customer.length > 0) {
        return true
      }
      return false
    },
    eventTypes() {
      if (this.hasMultipleCustomers || this.isLeadPN) {
        return _.filter(this.appointmentEventTypes, type => appointmentTypes.PPN_APPOINTMENTS.includes(type.id))
      }
      return _.filter(this.appointmentEventTypes, type => appointmentTypes.PCL_APPOINTMENTS.includes(type.id))
    },
    specialistConsultantsList() {
      if (!this.specialistConsultants || this.specialistConsultants.length === 0) {
        return []
      }

      const uniqueConsultants = _.uniqBy(this.specialistConsultants, 'id')

      if (this.hasMultipleCustomers || this.isLeadPN) {
        return uniqueConsultants.map(consultant => ({
          ...consultant,
          displayName: consultant.name,
        }))
      }
      return uniqueConsultants.map(consultant => ({
        ...consultant,
        displayName: `${consultant.role_name} - ${consultant.name}`,
      }))
    },
    selectedConsultant() {
      if (!this.consultantSpecialistId) return null

      if (!this.specialistConsultants) return null

      return this.specialistConsultants.find(consultant => consultant.id === this.consultantSpecialistId)
    },
    selectedCustomers() {
      const ids = this.hasMultipleCustomers ? this.customer : [this.customer]
      return _.filter(this.customers, customer => _.includes(ids, customer.id))
    },
    otherOwnersNames() {
      const loggedConsultantId = this.user?.appify_consultant_id
      if (!loggedConsultantId) return []
      return _.uniq(
        _.filter(
          _.map(this.selectedCustomers, customer => (customer.consultant_id
            && customer.consultant_id !== loggedConsultantId
            ? customer.consultant?.name || 'outro PN'
            : null)),
        ),
      )
    },
    isProductionOfAnotherOwner() {
      return this.otherOwnersNames.length > 0
    },
    otherOwnersLabel() {
      return this.otherOwnersNames.join(', ')
    },
  },
  watch: {
    // A regra do início no passado compara com o "agora" da última mudança (e do salvar).
    appointmentStart() {
      this.refreshNow()
    },
    eventType(newValue) {
      this.consultantSpecialistId = null

      if (newValue) {
        this.loadSpecialistConsultants(newValue)
      } else {
        this.consultantSpecialist = undefined
      }
    },
  },
  mounted() {
    this.loadNewAppointmentOptions()
  },
  methods: {
    ...mapMutations({
      mutateCustomers: types.MUTATE_APPOINTMENT_CUSTOMERS,
    }),
    ...mapActions({
      getEventTypes: types.GET_APPOINTMENT_EVENT_TYPES,
      getMeetingFormatTypes: types.GET_APPOINTMENT_MEETING_FORMAT_TYPES,
      getCustomers: types.GET_APPOINTMENT_CUSTOMERS,
      storeAppointment: types.STORE_APPOINTMENT,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getSpecialistConsultants: sharedTypes.GET_SPECIALIST_CONSULTANTS,
      getCustomer: sharedTypes.GET_CUSTOMER,
    }),
    refreshNow() {
      this.nowTick = Date.now()
    },
    // Início que já passou (antes do minuto atual), salvo o que já estava gravado no reagendamento. O servidor dá
    // 5 min de folga; aqui não, para o PN não escolher um horário que ele vê como passado.
    isStartInPast(value) {
      if (!value || value === this.originalAppointmentStart) return false
      const start = value instanceof Date ? moment(value) : moment(value, 'DD/MM/YYYY HH:mm')
      if (!start.isValid()) return false
      return start.isBefore(moment(this.nowTick).startOf('minute'))
    },
    // Tipos de compromisso, formatos de reunião e PNs (rótulo do PN Responsável) — no `mounted`.
    loadNewAppointmentOptions() {
      this.loading.eventTypes = true
      this.getEventTypes()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar os tipos de compromisso para seleção. Entre em contato com o setor de TI.'),
          })
        })
        .finally(() => {
          this.loading.eventTypes = false
        })

      this.loading.meetingFormatTypes = true
      this.getMeetingFormatTypes()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar os formatos de reunião para seleção. Entre em contato com o setor de TI.'),
          })
        })
        .finally(() => {
          this.loading.meetingFormatTypes = false
        })

      this.loading.consultants = true
      this.getConsultants()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.'),
          })
        })
        .finally(() => {
          this.loading.consultants = false
        })
    },
    // Especialistas elegíveis para o tipo de compromisso. Devolve a promise (nunca rejeita: erro vira toast).
    loadSpecialistConsultants(eventType) {
      this.loading.specialistConsultants = true

      return this.getSpecialistConsultants(specialistParamsForEventType(eventType))
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar os consultores especialistas para seleção. Entre em contato com o setor de TI.'),
          })
        })
        .finally(() => {
          this.loading.specialistConsultants = false
        })
    },
    // Carrega um cliente pelo id e o deixa como a única opção da lista (cliente pré-selecionado).
    // Resolve com o cliente, ou `null` se não veio nada; erro vira toast e resolve `null`.
    fetchAppointmentCustomer(customerId) {
      return this.getCustomer(customerId)
        .then(resp => {
          if (resp && resp.data) {
            this.mutateCustomers([resp.data])
            this.customer = resp.data.id
            return resp.data
          }
          return null
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar o cliente. Tente novamente ou realize uma busca pelo nome.'),
          })
          return null
        })
    },
    // Busca de clientes/leads elegíveis (`GET /api/customers_info/summary/appointment`). Resolve `true`/`false`.
    findCustomers(keyword) {
      this.loading.customers = true
      return this.getCustomers({ keyword })
        .then(() => true)
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: errorToastProps('Ocorreu um erro ao carregar os clientes para seleção. Entre em contato com o setor de TI.'),
          })
          return false
        })
        .finally(() => {
          this.loading.customers = false
        })
    },
    // Momento X não é selecionável para lead de cliente (lead PN e agendamento múltiplo não têm a restrição).
    disableItem(option) {
      if (this.hasMultipleCustomers) return true
      const selected = this.selectedCustomer
      if (!selected) {
        return false
      }
      if (
        !this.isLeadPN
        && selected.customer_type_id === customerType.LEAD
        && option.id === appointmentTypes.X_MOMENT
      ) {
        return false
      }
      return true
    },
    // Especialista vinculado agendando para cliente de outro PN: confirma que a produção vai para o dono.
    async confirmOtherOwnerProduction() {
      if (!this.isProductionOfAnotherOwner) return true
      const result = await this.$swal({
        title: 'Produção do dono do cliente',
        html: `Você está registrando uma atividade para um cliente pertencente a <strong>${_.escape(this.otherOwnersLabel)}</strong>. Para fins de carteira, indicadores e produção, este registro será atribuído ao dono do cliente. Você continuará identificado como responsável pela execução.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        background: `${this.skin === 'dark' ? '#283046' : ''}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-secondary ml-1',
          htmlContainer: `${this.skin === 'dark' ? 'text-white' : ''}`,
        },
      })
      return !!result.value
    },
    // Corpo de `POST /api/appointments` (ou PUT no reagendamento, `.../multiples` no múltiplo).
    buildAppointmentPayload() {
      return {
        id: this.id,
        customer_id: this.customer,
        customer_email: this.customerEmail,
        event_type: this.eventType,
        meeting_format: this.meetingFormat,
        consultant_id: this.consultant,
        consultant_specialist_id: this.consultantSpecialistId,
        appointment_start: stringToDateTime(this.appointmentStart),
        appointment_end: stringToDateTime(this.appointmentEnd),
        meeting_room_link: this.roomLink,
        description: this.description,
        is_multiple: this.hasMultipleCustomers,
      }
    },
    // Envia o compromisso. A promise é a do STORE_APPOINTMENT: o interceptor pode resolvê-la com `undefined`
    // (401/403/419 — sessão expirada), então quem chama confere a resposta antes de tratar como sucesso.
    saveAppointment() {
      return this.storeAppointment(this.buildAppointmentPayload())
    },
    // Toast de falha ao salvar: 405 = regra de negócio (mensagem do backend); resto = erro genérico.
    notifyAppointmentSaveError(err) {
      if (err && err.response && err.response.status == 405) { // eslint-disable-line eqeqeq
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Oops!',
            text: `Não é possivel Salvar. ${err.response.data.message}`,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      } else {
        this.toast({
          component: ToastificationContent,
          props: errorToastProps('Ocorreu um erro ao salvar o compromisso. Entre em contato com o setor de TI.'),
        })
      }
    },
    // Limpa o formulário (a validação também).
    resetAppointmentForm() {
      this.customer = undefined
      this.customerEmail = undefined
      this.eventType = undefined
      this.meetingFormat = undefined
      this.consultant = undefined
      this.consultantSpecialist = undefined
      this.consultantSpecialistId = undefined
      this.appointmentStart = undefined
      this.appointmentEnd = undefined
      this.roomLink = undefined
      this.description = undefined
      this.id = undefined
      this.originalAppointmentStart = undefined
      this.mutateCustomers(undefined)
      this.v$.$reset()
    },
  },
}
